import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiEdit3, FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import mainPageTextService from "../../services/mainPageTextService";
import {
  buildEditorHtml,
  extractEditorValues,
  normalizeRichText,
} from "./richText";
import "./style.css";

const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
]
  .filter(Boolean)
  .map((adminEmail) => adminEmail.trim().toLowerCase());

export const useMainPageText = (section, defaults) => {
  const [text, setText] = useState(defaults);

  useEffect(() => {
    let active = true;

    mainPageTextService
      .getAll()
      .then((content) => {
        if (!active || !content?.[section]) return;
        const saved = content[section];
        const allowedSavedText = Object.keys(defaults).reduce((result, key) => {
          if (typeof saved[key] === "string") result[key] = saved[key];
          return result;
        }, {});
        setText((current) => ({ ...current, ...allowedSavedText }));
      })
      .catch(() => {
        // The curated defaults remain visible when content cannot be loaded.
      });

    return () => {
      active = false;
    };
  }, [section, defaults]);

  return [text, setText];
};

export const FormattedMainPageText = ({ text }) => (
  <span dangerouslySetInnerHTML={{ __html: normalizeRichText(text) }} />
);

const MainPageTextEditor = ({
  section,
  sectionLabel,
  fields,
  value,
  onSaved,
}) => {
  const email = useSelector((state) => state?.user?.user?.email);
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const isAdmin = !!normalizedEmail && ADMIN_EMAILS.includes(normalizedEmail);
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorHtml, setEditorHtml] = useState("");
  const editorRef = useRef(null);

  const editableFields = useMemo(
    () => fields.filter((field) => Object.prototype.hasOwnProperty.call(value, field.key)),
    [fields, value]
  );

  useEffect(() => {
    if (isOpen) setEditorHtml(buildEditorHtml(editableFields, value));
  }, [editableFields, isOpen, value]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!isAdmin) return null;

  const applyFormat = (command) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const sanitized = extractEditorValues(
        editorRef.current?.innerHTML || editorHtml,
        editableFields
      );
      await mainPageTextService.saveSection(section, sanitized, normalizedEmail);
      onSaved(sanitized);
      // toast.success("Textul a fost salvat.");
      setIsOpen(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error.message ||
        "Textul nu a putut fi salvat."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="main-page-text-edit__trigger"
        onClick={() => setIsOpen(true)}
        aria-label={`Editează textul secțiunii ${sectionLabel}`}
        title={`Editează textul secțiunii ${sectionLabel}`}
      >
        <FiEdit3 size={16} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className="main-page-text-edit__overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`main-page-text-title-${section}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <div className="main-page-text-edit__modal">
            <header className="main-page-text-edit__header">
              <div>
                <span>CONȚINUT PAGINĂ PRINCIPALĂ</span>
                <h3 id={`main-page-text-title-${section}`}>Editează {sectionLabel}</h3>
              </div>
              <button type="button" onClick={() => setIsOpen(false)} aria-label="Închide">
                <FiX size={18} aria-hidden="true" />
              </button>
            </header>

            <div className="main-page-text-edit__body">
              <p className="main-page-text-edit__hint">
                Editează conținutul într-un singur câmp. Pre-titlul, titlul, subtitlul și butoanele rămân neschimbate.
              </p>
              <div className="main-page-text-edit__toolbar" role="toolbar" aria-label="Formatare text">
                <button
                  type="button"
                  aria-label="Text îngroșat"
                  title="Text îngroșat"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    applyFormat("bold");
                  }}
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  aria-label="Text italic"
                  title="Text italic"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    applyFormat("italic");
                  }}
                >
                  <em>I</em>
                </button>
              </div>
              <div
                ref={editorRef}
                className="main-page-text-edit__editor"
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label={`Conținut editabil pentru ${sectionLabel}`}
                dangerouslySetInnerHTML={{ __html: editorHtml }}
              />
            </div>

            <footer className="main-page-text-edit__footer">
              <button type="button" className="main-page-text-edit__cancel" onClick={() => setIsOpen(false)}>
                Anulează
              </button>
              <button type="button" className="main-page-text-edit__save" onClick={save} disabled={saving}>
                {saving ? "Se salvează…" : "Salvează textul"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default MainPageTextEditor;
